// Types, labels and rules shared by the server, the web app and (later) the
// MCP server. Pure TypeScript - no database or Node imports - so the browser
// can use it too.

export type Status = "open" | "closed";
/** The fix flow for an open item. Verified items are also closed and archived. */
export type Stage = "todo" | "developed" | "deployed" | "verified";
export type Priority = "high" | "normal" | "low";
export type ReactionKind = "agree" | "question" | "claim";
export type MemberRole = "lead" | "dev" | "reporter" | "viewer";

/** Item tags. Most say where in the product it happens ("Where" filter); `teams`
 *  instead marks an item captured from Teams and lives under the Teams filter. */
export const TAGS = [
  { id: "b2bstore", label: "Business Storefront", short: "business storefront", area: true },
  { id: "custstore", label: "Customer Storefront", short: "customer storefront", area: true },
  { id: "webpage", label: "Webpage", short: "webpage", area: true },
  { id: "app", label: "App", short: "app", area: true },
  { id: "brokered", label: "Brokered", short: "brokered", area: true },
  { id: "delivery", label: "Delivery", short: "delivery", area: true },
  { id: "teams", label: "From Teams", short: "from teams", area: false },
  { id: "streetvehicle", label: "Street Vehicle", short: "street vehicle", area: true },
] as const;

/** Tags people choose as "Where" - everything except the Teams marker. */
export const AREA_TAGS = TAGS.filter((t) => t.area);

/** "Who hits it" filter - the kind of user who runs into the item. */
export const HIT_ROLES = [
  { id: "B2B Customer", label: "Customer" },
  { id: "Sales Broker", label: "Broker" },
  { id: "Delivery Driver", label: "Driver" },
  { id: "Receiver", label: "Receiver" },
  { id: "Parts Puller", label: "Puller" },
  { id: "Inventory", label: "Inventory" },
  { id: "Retail Customer", label: "Retail Customer" },
] as const;

export const REACTIONS: [ReactionKind, string][] = [
  ["agree", "Agree"],
  ["question", "Question"],
  ["claim", "Claim"],
];

export function tagShort(id: string): string {
  return TAGS.find((t) => t.id === id)?.short ?? id;
}

// -- Boards (organisations, docs/design/orgs.md) ------------------------------
// OEMUAP is the company board and holds everything from before; everyone gets a personal board.

export type OrgKind = "company" | "personal";
/** a person's role on a personal board (OEMUAP uses the company roles instead) */
export type OrgRole = "owner" | "editor" | "viewer";

/** OEMUAP: the company org's id (made by migration M1) and its slug (?org=oemuap, or no ?org=) */
export const COMPANY_ORG_ID = "org_oemuap";
export const COMPANY_SLUG = "oemuap";

/** Which board a viewer is looking at, and their role on it. */
export interface ViewerOrg {
  kind: OrgKind;
  /** personal boards: owner / editor / viewer; OEMUAP: null (the company roles apply) */
  role: OrgRole | null;
  /** its last owner left the company: read-only for everyone (orgs.md 1.5) */
  frozen?: boolean;
}

// -- Item ids and numbers (orgs.md 1.2) -----------------------------------------
// An item has an internal id (Item.id), which every other table points at, and its # on its board
// (Item.num), which is what people, URLs, Claude and Teams use. They're equal for every item made
// before the key change (M2); new ids start at 1,000,001, so mixing them up fails at once.

/** An item's internal id: never shown, never in a URL or a payload. */
export type ItemId = number & { readonly __item: "id" };
/** An item's # on its board. Plain numbers are #s; an ItemId can't be passed as one. */
export type ItemNum = number & { readonly __item?: "num" };
/** A number known to be an Item.id (e.g. a row's itemId column). */
export const asItemId = (n: number) => n as ItemId;
/** An item by both: what the history and live updates need. */
export interface ItemRef {
  id: number;
  num: number;
}

/** The board a payload belongs to, as the web app shows it. */
export interface BoardOrg {
  slug: string;
  name: string;
  kind: OrgKind;
  /** its last active owner left the company: read-only for everyone until someone is made its owner (orgs.md 1.5) */
  frozen?: { since: string; formerOwners: string[] } | null;
}

/** What ?org= says for "my own board", whoever opens it; the web app replaces it with the board's slug. */
export const MY_BOARD_SLUGS = ["me", "personal"] as const;
export const isMyBoardSlug = (s: string | null | undefined) => (MY_BOARD_SLUGS as readonly string[]).includes(s?.trim().toLowerCase() ?? "");

/** One row of the board switcher (GET /api/orgs). */
export interface BoardListing {
  slug: string;
  name: string;
  kind: OrgKind;
  /** items on it (the archive included) */
  items: number;
  /** the viewer's role there; null on OEMUAP */
  role: OrgRole | null;
  /** the board made for the viewer ("My board") */
  mine: boolean;
  /** false for "My board" before its first open: opening it makes it */
  exists: boolean;
  /** the other people on it, by name (owners first) */
  people: string[];
  /** a board shared with the viewer: who invited them */
  invitedBy: string | null;
  /** its last owner left the company: read-only (orgs.md 1.5) */
  frozen: boolean;
}

/** The switcher: the viewer's boards (OEMUAP, theirs), and the ones others shared with them. */
export interface BoardsList {
  boards: BoardListing[];
  shared: BoardListing[];
  /** personal boards are switched on for this person (PERSONAL_BOARDS) */
  personal: boolean;
}

/** The roles on a personal board, as the People screen offers them (orgs.md 1.4, 5). */
export const ORG_ROLES: { id: OrgRole; label: string; can: string }[] = [
  { id: "owner", label: "Owner", can: "everything: invite and remove people, change anyone's role, link to OEMUAP and send updates, rename or close the board" },
  { id: "editor", label: "Can edit", can: "everything on the items, projects and the archive; their own pins" },
  { id: "viewer", label: "Can view", can: "reads the board, its archive and History; their own pins" },
];
export const isOrgRole = (v: unknown): v is OrgRole => v === "owner" || v === "editor" || v === "viewer";

/** One person on a personal board, as its People screen lists them. */
export interface BoardPerson {
  memberId: string;
  name: string;
  role: OrgRole;
  /** who invited them (null: they made the board, or it was handed to them) */
  invitedBy: string | null;
  invitedAt: string;
  /** the person it was made for ("made the board") */
  creator: boolean;
  /** they left the company (deactivated): an owner here is a former owner (orgs.md 1.5) */
  leftCompany: boolean;
}

/** A line of a board's membership history (OrgEvent). */
export interface BoardPeopleEvent {
  at: string;
  actor: string | null;
  kind: string;
  text: string;
}

/** A board's People screen: who's on it, who could be invited, its history. */
export interface BoardPeople {
  people: BoardPerson[];
  /** owners who left the company: named, not counted (orgs.md 1.5) */
  formerOwners: BoardPerson[];
  /** active members who can sign in and aren't on the board: the invite picker */
  invitable: { id: string; name: string; role: MemberRole }[];
  /** newest first */
  events: BoardPeopleEvent[];
  frozenAt: string | null;
}

/**
 * A personal board as a platform admin sees it (orgs.md 1.4): its name, owners current and former,
 * when it froze or closed, how many items it has and who is on it; never its items.
 */
export interface PlatformBoard {
  id: string;
  slug: string;
  name: string;
  /** the person it was made for */
  madeFor: string | null;
  owners: string[];
  formerOwners: string[];
  people: { name: string; role: OrgRole }[];
  items: number;
  frozenAt: string | null;
  closedAt: string | null;
  /** its items were moved to this board */
  mergedInto: string | null;
}

/** The platform admin's list: boards that need an owner, and closed boards (to restore). */
export interface PlatformBoards {
  frozen: PlatformBoard[];
  closed: PlatformBoard[];
  /** boards their items can be moved to: personal, open, not frozen (name and slug only) */
  targets: { id: string; name: string; madeFor: string | null }[];
  /** who can be made an owner: active members who can sign in */
  people: { id: string; name: string }[];
}

/** What a membership event says on the People screen. Pure. */
export function describeOrgEvent(kind: string, data: Record<string, unknown>): string {
  const who = typeof data.name === "string" ? data.name : "someone";
  const role = (r: unknown) => ORG_ROLES.find((x) => x.id === r)?.label.toLowerCase() ?? String(r);
  switch (kind) {
    case "created":
      return "made the board";
    case "invited":
      return `invited ${who} (${role(data.role)})${data.again ? ", back on the board" : ""}`;
    case "role_changed":
      return `made ${who} ${role(data.to)} (was ${role(data.from)})${data.platform ? ", as a platform admin" : ""}`;
    case "left":
      return "left the board";
    case "removed":
      return `removed ${who}`;
    case "frozen":
      return `${who} left the company: the board is read-only until someone is made its owner`;
    case "unfrozen":
      return `the board has an owner again (${who}): it can be changed again`;
    case "renamed":
      return `renamed the board "${String(data.to ?? "")}"`;
    case "merged":
      return data.into ? `moved its ${data.items ?? ""} items to ${data.into}`.replace("  ", " ") : `took in ${data.items ?? ""} items from ${data.from ?? "another board"}`.replace("  ", " ");
    case "closed":
      return "closed the board";
    case "restored":
      return "restored the board";
    default:
      return kind;
  }
}

/** "Private · you and Jake", "Private · only you": who's on a personal board, as its chip says. */
export function privateLabel(people: string[]): string {
  if (!people.length) return "Private · only you";
  if (people.length === 1) return `Private · you and ${people[0]}`;
  return `Private · you and ${people.length} others`;
}

// -- People and permissions -------------------------------------------------

export interface Viewer {
  id: string;
  name: string;
  email: string | null;
  role: MemberRole;
  isAdmin: boolean;
  canBuild: boolean;
  canApprove: boolean;
  /** checks fixes after they're deployed; can record steps nobody marked (see "stage_for") */
  isVerifier: boolean;
  /** above the company roles, for personal boards only: hand over and move a frozen board, close or restore one (orgs.md 1.4) */
  isPlatformAdmin?: boolean;
  /** the board they're looking at (see orgViewer); absent means OEMUAP */
  org?: ViewerOrg;
}

/**
 * The person as a board sees them (orgs.md 1.4). On OEMUAP: their company role and flags. On a
 * personal board the company role gives nothing: owners and editors act as leads there (so a
 * one-person board can verify its own fixes), viewers only read, and nobody is a company admin.
 */
export function orgViewer(v: Viewer, org: { kind: OrgKind; frozen?: boolean }, role: OrgRole | null = null): Viewer {
  if (org.kind === "company") return { ...v, org: { kind: "company", role: null } };
  const reads = !role || role === "viewer" || !!org.frozen;
  return {
    ...v,
    role: reads ? "viewer" : "lead",
    isAdmin: false,
    canBuild: false,
    canApprove: false,
    isVerifier: false,
    org: { kind: "personal", role, ...(org.frozen ? { frozen: true } : {}) },
  };
}

export type Action =
  | "create" // log a new item
  | "react" // agree / question
  | "note" // add a note (and edit or remove your own: see canEditNote, canRemoveNote)
  | "edit" // change an item's title and description (whoever logged it, and leads: see canEditItem)
  | "draft" // make an item a draft, or ready for the team (whoever logged it, and leads: canSetDraft, OEM-286)
  | "close" // close or reopen (a project: leads only - see canClose)
  | "claim" // take an item yourself
  | "assign" // put anyone on any item or take them off (leads and devs); whoever is on an item may too (canAssignItem)
  | "nondev" // move an item in or out of the developers' list
  | "teamsres" // mark that the fix was agreed or confirmed in Teams
  | "request" // ask Claude to build a feature (a GitHub issue)
  | "work" // start, log progress on and finish a work session (e.g. from Claude Code)
  | "meeting" // mark that the team meeting happened ("since the last meeting")
  | "attach" // add screenshots and screen recordings
  | "stage" // move an item along the fix flow: developed, deployed, or back to To do
  | "verify" // mark a deployed fix verified (it then moves to the archive) - see canVerify
  | "stage_for" // leads and verifiers: verify or deploy an item whose earlier steps nobody marked, naming who did them
  | "archive" // take items off the board into the archive, or restore them
  | "organize" // set what an item is (feature, bug, task) and what it sits under
  | "project" // make an item a project, or give a project its own board
  | "lock" // admins: lock an item to its project so it can't be moved to another
  | "admin" // the team admin screen: add people, change roles and flags, deactivate; Settings → GitHub (the connection, deploy signals, autolinks, main-board repos)
  | "settings" // open Settings (the main board's cogwheel): admins all of it, leads the GitHub tab's repos, to link them to projects
  | "github" // link a pull request to an item by its URL, or unlink one (OEMUAP only: docs/github.md)
  | "view_shared" // saved views (OEM-268): make views shared with the board, and rename, change, reorder or delete shared ones (anyone makes their own)
  // personal boards (orgs.md 1.4)
  | "invite" // the People screen: invite, remove, change roles
  | "bridge" // link to an OEMUAP item, create one from this, Send updates
  | "close_board" // close ("delete") the board
  | "copy_out" // "Copy to my board": take an item from this board into your own (owners and editors, a frozen board too)
  | "platform"; // a platform admin's: roles on a frozen board (the hand-over), moving its items, closing and restoring

/** OEMUAP-only actions: off on every personal board. */
export const COMPANY_ONLY: readonly Action[] = ["admin", "settings", "request", "teamsres", "nondev", "github"];
/** Personal-board actions only its owners have. */
const OWNER_ONLY: readonly Action[] = ["invite", "bridge", "lock", "close_board"];

/** The one place that decides who may do what. The server enforces it; the UI uses it to hide buttons. */
export function can(v: (Pick<Viewer, "role" | "isAdmin" | "org"> & { canBuild?: boolean; isVerifier?: boolean; isPlatformAdmin?: boolean }) | null | undefined, action: Action): boolean {
  if (!v) return false;
  // above the company roles, and nothing else: no board's items, no company admin's powers
  if (action === "platform") return !!v.isPlatformAdmin;
  // copying out reads the board and writes on your own: owners and editors, a frozen board included (cleaning up, orgs.md 1.5);
  // on OEMUAP everyone on the team can read every item, so anyone may copy one into their own board (6.2)
  if (action === "copy_out") return v.org?.kind === "personal" ? v.org.role === "owner" || v.org.role === "editor" : true;
  if (v.org?.kind === "personal") {
    if (v.org.frozen) return false; // read-only for everyone (pins are not an Action)
    if (COMPANY_ONLY.includes(action)) return false;
    if (OWNER_ONLY.includes(action)) return v.org.role === "owner";
    return v.org.role === "owner" || v.org.role === "editor"; // everything else on the board
  }
  // nothing to invite to or send to on OEMUAP, and it can't be closed
  if (action === "invite" || action === "bridge" || action === "close_board") return false;
  if (v.isAdmin) return true;
  switch (action) {
    case "request":
      return !!v.canBuild && v.role !== "viewer";
    case "stage_for":
      return v.role === "lead" || (!!v.isVerifier && v.role !== "viewer");
    case "admin":
    case "lock":
      return false;
    // put anyone on any item, take anyone off (OEM-285): leads and developers. Whoever is on an item may
    // change who has that one too (canAssignItem)
    case "assign":
      return v.role === "lead" || v.role === "dev";
    case "nondev":
    case "teamsres":
    case "meeting":
    case "archive":
    case "project":
    case "settings":
    case "view_shared":
      return v.role === "lead";
    default:
      return v.role !== "viewer";
  }
}

// -- The fix flow and the archive -----------------------------------------------

/**
 * Where an item sits on the board: what's left to do, the PR review queue (OEMUAP: a pull request is
 * open and ready for review), the deploy queue, the verification queue, or closed. PR review isn't a
 * stage: it's read from the item's linked PRs (flowLane, inReview), so the fix flow is unchanged.
 */
export type Lane = "todo" | "review" | "deploy" | "verify" | "closed";

export const LANES: { id: Lane; label: string; hint: string }[] = [
  { id: "todo", label: "To do", hint: "Open items nobody has marked developed yet, with no pull request ready for review" },
  { id: "review", label: "PR review", hint: "A pull request for it is open and ready for review: waiting for a reviewer. It moves to the deploy queue when it merges" },
  { id: "deploy", label: "Deploy queue", hint: "Developed, waiting to be deployed" },
  { id: "verify", label: "Verify queue", hint: "Deployed, waiting for someone to check the fix" },
  { id: "closed", label: "Closed", hint: "Closed without going through verification, or verified and restored from the archive" },
];

/** The queues a board has: PR review is OEMUAP's (GitHub); a personal board has the others. */
export const lanesFor = (company: boolean) => (company ? LANES : LANES.filter((l) => l.id !== "review"));

export const STAGE_LABEL: Record<Stage, string> = { todo: "To do", developed: "Developed", deployed: "Deployed", verified: "Verified" };

export function laneOf(it: { status: Status; stage: Stage }): Lane {
  if (it.status === "closed") return "closed";
  return it.stage === "developed" ? "deploy" : it.stage === "deployed" ? "verify" : "todo";
}

/** Who did a step of the fix flow, when, and what they said. */
export interface StageStamp {
  at: string;
  by: string | null;
  byId: string | null;
  note: string | null;
  /** nobody marked the step at the time: who later recorded that `by` did it (a lead or a verifier) */
  recordedBy?: string | null;
}

/** A step a lead or a verifier can record for someone else, when nobody marked it. */
export type RecordedStep = "developed" | "deployed";

const STAGE_RANK: Record<Stage, number> = { todo: 0, developed: 1, deployed: 2, verified: 3 };

/**
 * The earlier steps nobody marked when an item moves forward to `to`: marking it deployed
 * needs a developer on record, verifying it a developer and a deploy. `developed` and
 * `deployed` are the item's stamps (anything truthy: the step is on record). Only leads and
 * verifiers may move it on anyway (can "stage_for"), naming the one person who did them.
 */
export function missingSteps(it: { stage: Stage; developed: unknown; deployed: unknown }, to: Stage): RecordedStep[] {
  if (to !== "deployed" && to !== "verified") return [];
  if (STAGE_RANK[to] <= STAGE_RANK[it.stage]) return [];
  const out: RecordedStep[] = [];
  if (STAGE_RANK[it.stage] < 1 || !it.developed) out.push("developed");
  if (to === "verified" && (STAGE_RANK[it.stage] < 2 || !it.deployed)) out.push("deployed");
  return out;
}

/** "developed", "deployed", "developed and deployed": the steps, as words. */
export const stepWords = (steps: RecordedStep[]) => steps.join(" and ");

/**
 * Verifying a fix: anyone who can change items, except whoever developed it -
 * a fix is checked by a second person. Leads (and admins) can verify their own
 * when there's nobody else.
 */
export function canVerify(v: (Pick<Viewer, "id" | "role" | "isAdmin"> & { canBuild?: boolean; isVerifier?: boolean }) | null | undefined, it: { developed: Pick<StageStamp, "byId"> | null }): boolean {
  if (!v || !can(v, "verify")) return false;
  if (v.isAdmin || v.role === "lead") return true;
  return !it.developed?.byId || it.developed.byId !== v.id;
}

// -- Who has an item: several people (OEM-285) ---------------------------------------
//
// An item can be with several people at once. The first of them is its lead (BoardItem.assignee,
// Item.assigneeId), so everything that reads one person keeps working; `assignees` is everyone. Leads
// and developers put anyone on any item and take anyone off; whoever is on an item may change who has
// that one (add people, take people off, themselves too, or hand it over). Claim adds yourself, Give
// back takes you off. Viewers can't.

/** One of the people an item is with: since when, who put them on it, and the next step for them. */
export interface BoardAssignee {
  id: string;
  name: string;
  at: string | null;
  by: string | null;
  note: string | null;
}

/** Everyone an item is with, the lead first (an older payload has only `assignee`). */
export const assigneesOf = (it: { assignee: BoardAssignee | null; assignees?: BoardAssignee[] }): BoardAssignee[] =>
  it.assignees ?? (it.assignee ? [it.assignee] : []);

/** The item is with this person (one of its people). */
export const isAssignee = (it: { assignee: BoardAssignee | null; assignees?: BoardAssignee[] }, memberId: string | null | undefined): boolean =>
  !!memberId && assigneesOf(it).some((a) => a.id === memberId);

/** Their names, the lead first. */
export const assigneeNames = (it: { assignee: BoardAssignee | null; assignees?: BoardAssignee[] }): string[] => assigneesOf(it).map((a) => a.name);

/** "Rohit", "Rohit and Vishal", "Rohit, Vishal and Jake"; "" for nobody. */
export function nameList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * Changing who has this item (OEM-285): leads and developers on any item (can "assign"); anyone else who
 * may claim items, on an item they're on (`ids`: who has it now). Claiming a free item or giving back your
 * own is claim / unclaim, which anyone who may claim can do.
 */
export function canAssignItem(v: (Pick<Viewer, "id" | "role" | "isAdmin" | "org"> & { canBuild?: boolean }) | null | undefined, ids: readonly string[]): boolean {
  if (!v) return false;
  return can(v, "assign") || (can(v, "claim") && ids.includes(v.id));
}

/** How a change of people is asked for: the whole list (set), or some to put on (add) or take off (remove). */
export type AssignMode = "set" | "add" | "remove";

/**
 * Who has an item after a change, the lead first: `current` (the lead first) changed by `mode` with
 * `people` (member ids, in the order given). The lead stays the lead while they're still on it; else
 * the first of the new list leads. Pure.
 */
export function nextAssignees(current: readonly string[], mode: AssignMode, people: readonly string[]): string[] {
  const want = [...new Set(people)];
  let next: string[];
  if (mode === "add") next = [...current, ...want.filter((p) => !current.includes(p))];
  else if (mode === "remove") next = current.filter((p) => !want.includes(p));
  else next = want;
  const lead = current[0];
  return lead && next.includes(lead) ? [lead, ...next.filter((p) => p !== lead)] : next;
}

/**
 * A change of people as the history says it (OEM-285), from an "assign" event's data: "assigned it to Rohit
 * and Vishal", "also put Vishal on it", "took Rohit off", "handed it from Rohit to Vishal", "unassigned it".
 * Older events (one person, `to`) read as before. The next step follows when there is one.
 */
export function assignText(data: Record<string, unknown>): string {
  const names = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : null);
  const added = names(data.added);
  const removed = names(data.removed);
  const people = names(data.people);
  const next = data.note ? ` — next step: ${data.note}` : "";
  if (!added || !removed || !people) return `${data.to ? `assigned it to ${data.to}` : "unassigned it"}${next}`;
  const before = people.filter((p) => !added.includes(p)).concat(removed);
  const stay = people.filter((p) => !added.includes(p));
  let text: string;
  if (!people.length) text = removed.length && before.length > 1 ? `unassigned it (took ${nameList(removed)} off)` : "unassigned it";
  else if (added.length && removed.length) text = `handed it from ${nameList(removed)} to ${nameList(added)}${stay.length ? ` (still with ${nameList(stay)})` : ""}`;
  else if (added.length) text = stay.length ? `also put ${nameList(added)} on it (with ${nameList(stay)})` : `assigned it to ${nameList(added)}`;
  else if (removed.length) text = `took ${nameList(removed)} off (still with ${nameList(people)})`;
  else text = `assigned it to ${nameList(people)}`;
  return `${text}${next}`;
}

// -- Projects and sub-items -------------------------------------------------------
//
// Jira's levels: a project groups features, bugs and tasks, and those can have
// sub-items. Projects are always at the top, and sub-items have no children of their
// own, so there are at most three levels and never a loop. A project can have its
// own board: its items then stay off the main board (Board, the board's own work).

export type ItemKind = "project" | "feature" | "bug" | "task";

export const KINDS: { id: ItemKind; label: string; hint: string }[] = [
  { id: "project", label: "Project", hint: "Groups features, bugs and tasks, with a board of its own" },
  { id: "feature", label: "Feature", hint: "Something new to build" },
  { id: "bug", label: "Bug", hint: "Something that doesn't work as it should" },
  { id: "task", label: "Task", hint: "Work to do that isn't a feature or a bug" },
];

export const kindLabel = (k: ItemKind | null | undefined) => KINDS.find((x) => x.id === k)?.label ?? "Item";

/** What the structure rules need to know about an item. */
export interface StructNode {
  num: number;
  kind: ItemKind | null;
  parentNum: number | null;
}

/** Index items for the structure rules: by number, and their children. */
export function structIndex(nodes: Iterable<StructNode>) {
  const byNum = new Map<number, StructNode>();
  const kids = new Map<number, StructNode[]>();
  for (const n of nodes) {
    byNum.set(n.num, n);
    if (n.parentNum != null) kids.set(n.parentNum, [...(kids.get(n.parentNum) ?? []), n]);
  }
  return { byNum, children: (num: number) => kids.get(num) ?? [] };
}
export type StructIndex = ReturnType<typeof structIndex>;

/**
 * How far along the items under something are (BoardItem.children): how many, and how many are
 * done (closed). An archived item that isn't done is left out of both - it was put away, not
 * finished - and counted in `archived`; an archived done one (verified) still counts as done.
 */
export function progressOf(kids: Iterable<{ status: Status; archived: boolean }>): { total: number; done: number; archived: number } {
  let total = 0;
  let done = 0;
  let archived = 0;
  for (const k of kids) {
    if (k.status === "closed") {
      total++;
      done++;
    } else if (k.archived) archived++;
    else total++;
  }
  return { total, done, archived };
}

/** Everything under an item, the archived ones that aren't done too: for what moves or copies with it. */
export const childCount = (it: Pick<BoardItem, "children">) => it.children.total + (it.children.archived ?? 0);

/** A sub-item: under a feature, bug or task (not directly under a project). */
export function isSubItem(ix: StructIndex, n: StructNode): boolean {
  if (n.parentNum == null) return false;
  const p = ix.byNum.get(n.parentNum);
  return !!p && p.kind !== "project";
}

/** The project an item is in (through its parent for a sub-item), or null. A project is in none. */
export function projectNumOf(ix: StructIndex, n: StructNode): number | null {
  if (n.kind === "project") return null;
  let cur: StructNode | undefined = n;
  for (let i = 0; i < 3 && cur?.parentNum != null; i++) {
    const p = ix.byNum.get(cur.parentNum);
    if (!p) return null;
    if (p.kind === "project") return p.num;
    cur = p;
  }
  return null;
}

/**
 * The item whose lock keeps `num` in the project it's in: the item itself, or the item it
 * sits under (its parent, and so on up to the project). Sub-items go wherever their parent
 * goes, so a lock on the parent covers them too. A project is locked to nothing, so the walk
 * stops there. null when nothing in the chain is locked. Use the structure from BEFORE a
 * move: it's the parent the item is leaving whose lock counts.
 */
export function lockHolder(ix: StructIndex, num: number, isLocked: (num: number) => boolean): number | null {
  if (isLocked(num)) return num;
  let cur = ix.byNum.get(num);
  for (let i = 0; i < 3 && cur?.parentNum != null; i++) {
    const p = ix.byNum.get(cur.parentNum);
    if (!p || p.kind === "project") return null;
    if (isLocked(p.num)) return p.num;
    cur = p;
  }
  return null;
}

/**
 * Whether a move breaks a lock: `num` changes project between `before` and `after` while
 * it, or the item it sat under, is locked. Returns the locked item (`holder`) and the
 * project it was in, or null when the move is fine.
 */
export function lockedMove(before: StructIndex, after: StructIndex, num: number, isLocked: (num: number) => boolean): { holder: number; from: number | null } | null {
  const was = before.byNum.get(num);
  const now = after.byNum.get(num);
  if (!was || !now) return null;
  const from = projectNumOf(before, was);
  if (from === projectNumOf(after, now)) return null;
  const holder = lockHolder(before, num, isLocked);
  return holder == null ? null : { holder, from };
}

const ref = (n: number) => `#${n}`;

/**
 * Why item `num` can't be put under `parentNum` (null: take it out from under anything),
 * or null when it can.
 */
export function parentProblem(ix: StructIndex, num: number, parentNum: number | null): string | null {
  const it = ix.byNum.get(num);
  if (!it) return `${ref(num)} isn't on the board.`;
  if (parentNum == null) return null;
  if (parentNum === num) return "An item can't be under itself.";
  const p = ix.byNum.get(parentNum);
  if (!p) return `${ref(parentNum)} isn't on the board.`;
  if (it.kind === "project") return `${ref(num)} is a project, and projects are always at the top.`;
  if (p.kind === "project") return null;
  // under a feature, bug or task: it becomes a sub-item
  if (isSubItem(ix, p)) return `${ref(parentNum)} is a sub-item itself, and sub-items can't have their own. Put it under ${ref(p.parentNum!)} instead.`;
  if (ix.children(num).length) return `${ref(num)} has sub-items of its own, so it can only go in a project.`;
  return null;
}

/** Why item `num` can't become `to` (null: unclassified), or null when it can. */
export function kindProblem(ix: StructIndex, num: number, to: ItemKind | null): string | null {
  const it = ix.byNum.get(num);
  if (!it) return `${ref(num)} isn't on the board.`;
  if (it.kind === to) return null;
  if (to === "project") {
    if (it.parentNum != null) return `${ref(num)} is under ${ref(it.parentNum)}. Take it out first: projects are always at the top.`;
    return null;
  }
  if (it.kind === "project") {
    // its items would become its sub-items, which can't have their own: say what to do
    const items = ix.children(num);
    if (items.some((c) => ix.children(c.num).length > 0)) {
      const n = items.length;
      return `Its ${n} ${n === 1 ? "item needs" : "items need"} a home first: move ${n === 1 ? "it" : "them"} to another project (or out of this one) before changing what it is.`;
    }
  }
  return null;
}

/** Where an item sits, for the board: its project and (for a sub-item) its parent. */
export interface ItemPlace {
  num: number;
  title: string;
  kind: ItemKind | null;
  /** its number in its project ("01-03"), when it has one (set on `parent`) */
  key?: string | null;
}

// -- What each kind does --------------------------------------------------------------
//
// A project has no flow of its own (it shows "n of m done"); a feature or a bug goes
// through the fix flow (To do -> Developed -> Deployed -> Verified); a task goes
// To do -> Done (closing it) and is never in the deploy or verify queue. Unclassified
// items (kind null) go through the fix flow, as everything did before kinds existed.

/** How an item moves: the fix flow, To do -> Done, or no flow at all. */
export type KindFlow = "fix" | "done" | "none";

export function kindFlow(kind: ItemKind | null | undefined): KindFlow {
  return kind === "project" ? "none" : kind === "task" ? "done" : "fix";
}

/**
 * laneOf with the kind's flow applied: a task or a project is never in the deploy or
 * verify queue, whatever its stage says (an item that became a task keeps no stage, but
 * this holds even for one that somehow did). A fix still To do with a linked PR open and
 * ready for review (OEMUAP, `prs`) is in the PR review queue (inReview). The queue chips,
 * the counts, the filters and the connector's `queue` all use this.
 */
export function flowLane(it: { status: Status; stage: Stage; kind?: ItemKind | null; archived?: unknown; prs?: Pick<BoardPr, "state" | "draft" | "moves">[] }): Lane {
  const lane = laneOf(it);
  if (lane === "todo") return inReview(it) ? "review" : "todo";
  return lane === "deploy" || lane === "verify" ? (kindFlow(it.kind) === "fix" ? lane : "todo") : lane;
}

/**
 * The buttons a card can have, by name. kindActions says which apply to a kind; whether
 * the viewer may use one is still can() (and the item's state: open, closed, archived).
 *
 *   openBoard    a project: show its items as the board
 *   openPanel    open it in a panel beside the board (any card, design 4.3)
 *   newItemHere  a project: log a new item in it
 *   react        Agree / Question
 *   claim        Claim / give it back
 *   note         Add a note
 *   attach       Add screenshot
 *   stage        the fix flow: Mark developed, Mark deployed, Send back
 *   verify       Verified (closes it and moves it to the archive)
 *   close        Close item / Reopen; on a task it reads Done / Reopen, on a project
 *                Close project / Reopen (see closeLabel)
 *   assign       hand it to someone
 *   nondev       Non-dev / Back to dev
 *   teamsres     Resolved via Teams
 *   organize     what it is and what it sits under
 *   link         Add link: it blocks, relates to or duplicates another item (in the strip
 *                while it has none; the Links block on the card has its own once it has some)
 *   addSubItem   log a sub-item under it
 *   history      everything that happened to it
 *   archive      Archive / Restore to board
 */
export const CARD_ACTIONS = [
  "openBoard",
  "openPanel",
  "newItemHere",
  "react",
  "claim",
  "note",
  "attach",
  "stage",
  "verify",
  "close",
  "assign",
  "nondev",
  "teamsres",
  "organize",
  "link",
  "addSubItem",
  "history",
  "archive",
] as const;
export type CardAction = (typeof CARD_ACTIONS)[number];

const PROJECT_ACTIONS: readonly CardAction[] = ["openBoard", "openPanel", "newItemHere", "organize", "note", "history", "close", "archive"];
// features, bugs and unclassified items: everything a card had before kinds, plus Add sub-item
const FIX_ACTIONS: readonly CardAction[] = CARD_ACTIONS.filter((a) => a !== "openBoard" && a !== "newItemHere");
// To do -> Done: no fix flow, no verify; Archive stays so a lead can file away a done task
const TASK_ACTIONS: readonly CardAction[] = ["openPanel", "react", "claim", "note", "attach", "close", "assign", "organize", "link", "history", "archive"];

/**
 * The card actions that apply to an item of this kind. A sub-item gets its kind's set
 * minus Add sub-item (sub-items can't have their own). Projects are never sub-items.
 */
export function kindActions(kind: ItemKind | null | undefined, isSubItem = false): ReadonlySet<CardAction> {
  const base = kind === "project" ? PROJECT_ACTIONS : kind === "task" ? TASK_ACTIONS : FIX_ACTIONS;
  return new Set(isSubItem ? base.filter((a) => a !== "addSubItem") : base);
}

/**
 * Who may close or reopen an item: anyone who can close items, but a project only leads
 * (and admins), the people who make and organize projects. setStatus enforces it, so the
 * web API and Claude's set_status get it too; the card's Close button applies the same rule.
 */
export function canClose(v: (Pick<Viewer, "role" | "isAdmin"> & { canBuild?: boolean }) | null | undefined, it: { kind?: ItemKind | null }): boolean {
  return can(v, "close") && (it.kind !== "project" || can(v, "project"));
}

/** What the close button says: a task is marked Done, a project is closed as a project. */
export function closeLabel(kind: ItemKind | null | undefined, status: Status): string {
  if (status === "closed") return "Reopen";
  return kind === "task" ? "Done" : kind === "project" ? "Close project" : "Close item";
}

// -- Project numbers ----------------------------------------------------------------
//
// Every item has its # number, which never changes. Projects also have their own
// number (01, 02, ...) and hand their items a number of their own: 01-07 is the 7th
// item project 01 numbered. That number belongs to the item for good: if it leaves
// the project, nobody else gets 01-07, and if it comes back it is 01-07 again.

/** "01" for project 1; three digits from 100. */
export const projectCode = (projectNo: number) => String(projectNo).padStart(2, "0");

/** "01-07": item 7 of project 01. */
export const keyLabel = (projectNo: number, seq: number) => `${projectCode(projectNo)}-${String(seq).padStart(2, "0")}`;

/** "01-07", "1-7" or "#01-07" -> project 1, item 7; anything else -> null. */
export function parseKey(s: string): { projectNo: number; seq: number } | null {
  const m = s.trim().replace(/^#/, "").match(/^(\d{1,4})-(\d{1,6})$/);
  if (!m) return null;
  const projectNo = Number(m[1]);
  const seq = Number(m[2]);
  return projectNo > 0 && seq > 0 ? { projectNo, seq } : null;
}

/** An item's number in the project it's in. */
export interface ItemKey {
  /** the project (its # number) */
  projectNum: number;
  projectNo: number;
  seq: number;
  /** "01-07" */
  label: string;
}

/**
 * How plain text (Claude, Teams) names an item outside any board: "01-07 (#185)" when it's
 * in a project, "Project 01 (#172)" for a project, "#188" otherwise. The # number is always
 * there: it's the one notes, Teams messages and old Claude output refer to.
 */
export function itemRef(it: { num: number; kind?: ItemKind | null; projectNo?: number | null; key?: { label: string } | string | null }): string {
  if (it.kind === "project" && it.projectNo != null) return `Project ${projectCode(it.projectNo)} (#${it.num})`;
  const key = typeof it.key === "string" ? it.key : it.key?.label;
  return key ? `${key} (#${it.num})` : `#${it.num}`;
}

// -- Pins -----------------------------------------------------------------------------

/** A panel someone pinned: it survives Reset and comes back after sign-in (GET /api/pins). */
export interface PinView {
  num: number;
  /** what the panel shows: a project's board, the item's card, or the item's notes */
  kind: "project" | "item" | "notes";
  title: string;
  /** what the item is now (project / feature / bug / task, null = not classified) */
  itemKind: ItemKind | null;
  /** a project's own number (01, 02, ...) */
  projectNo: number | null;
  /** its number in its project ("01-07"), if it's in one */
  key: string | null;
  status: Status;
  /** it's in the archive (the panel still opens) */
  archived: boolean;
  /** place in the person's list, from 0 */
  order: number;
  pinnedAt: string;
}

// -- Links between items ----------------------------------------------------------------
//
// One item blocks another, duplicates it, or relates to it (design 4.8). `blocks` and
// `duplicates` have a direction: "#170 blocks #167" reads "blocked by #170" on #167. `relates`
// reads the same both ways and is stored once. A link is on both items' cards and histories.

export type LinkKind = "blocks" | "relates" | "duplicates";
export const LINK_KINDS: readonly LinkKind[] = ["blocks", "relates", "duplicates"];
export const isLinkKind = (v: unknown): v is LinkKind => typeof v === "string" && (LINK_KINDS as readonly string[]).includes(v);

/** Which side of a link an item is on: `out` it is the link's `from` (it blocks / duplicates the other), `in` the other one is. */
export type LinkDir = "out" | "in";

/** How a link reads from one side: "blocks", "blocked by", "relates to", "duplicates", "duplicated by". */
export function linkLabel(kind: LinkKind, dir: LinkDir): string {
  if (kind === "relates") return "relates to";
  if (kind === "blocks") return dir === "out" ? "blocks" : "blocked by";
  return dir === "out" ? "duplicates" : "duplicated by";
}

/** What the Add link dialog offers, as what this item does to the other one. */
export const LINK_RELATIONS: { kind: LinkKind; dir: LinkDir; label: string; hint: string }[] = [
  { kind: "blocks", dir: "out", label: "blocks", hint: "The other item can't move on until this one is done" },
  { kind: "blocks", dir: "in", label: "is blocked by", hint: "This item can't move on until the other one is done" },
  { kind: "relates", dir: "out", label: "relates to", hint: "Worth reading together; neither waits on the other" },
  { kind: "duplicates", dir: "out", label: "duplicates", hint: "This is the same problem as the other item" },
  { kind: "duplicates", dir: "in", label: "is duplicated by", hint: "The other item is the same problem as this one" },
];

/** A link as one item's card shows it: the other item, and how this one relates to it. */
export interface BoardLink {
  id: string;
  kind: LinkKind;
  /** out: this item blocks / duplicates the other (or made the `relates` link); in: the other way round */
  dir: LinkDir;
  /** the other item */
  num: number;
  title: string;
  /** its number in its project ("01-07"), if it's in one */
  key: string | null;
  /** the project `key` is a number in (its # number) */
  projectNum: number | null;
  /** a project's own number, when the other item is a project */
  projectNo: number | null;
  itemKind: ItemKind | null;
  status: Status;
  /** the other item is in the archive */
  archived: boolean;
  assignee: string | null;
  /** everyone the other item is with, the lead first (OEM-285) */
  assignees?: string[];
  /** why they're linked (optional) */
  note: string | null;
  /** who linked them */
  by: string | null;
  byId: string | null;
  at: string;
}

/** A link as stored: from -> to. */
export interface LinkEnds {
  fromNum: number;
  toNum: number;
  kind: LinkKind;
}

/**
 * Why `from` can't be linked to `to` with `kind`, given the active links between the two, or null
 * when it can. Refuses a link to itself, the same link twice (a `relates` link from either side is
 * the same link), and the reverse of a `blocks` or `duplicates` link (two items can't block each other).
 */
export function linkProblem(from: number, to: number, kind: LinkKind, existing: LinkEnds[], name: (num: number) => string = (n) => `#${n}`): string | null {
  if (from === to) return "An item can't be linked to itself.";
  for (const l of existing) {
    if (l.kind !== kind) continue;
    const same = l.fromNum === from && l.toNum === to;
    const reverse = l.fromNum === to && l.toNum === from;
    if (same || (reverse && kind === "relates")) return `${name(l.fromNum)} already ${linkLabel(kind, "out")} ${name(l.toNum)}.`;
    if (reverse) return kind === "blocks" ? `${name(to)} already blocks ${name(from)}: two items can't block each other.` : `${name(to)} already duplicates ${name(from)}.`;
  }
  return null;
}

/** The ends of an item's links as stored, from its card's view of them. */
export const linkEnds = (num: number, links: Pick<BoardLink, "kind" | "dir" | "num">[]): LinkEnds[] =>
  links.map((l) => (l.dir === "out" ? { fromNum: num, toNum: l.num, kind: l.kind } : { fromNum: l.num, toNum: num, kind: l.kind }));

/** The open items blocking this one (incoming `blocks` from items that are still open). */
export const blockersOf = (links: BoardLink[]) => links.filter((l) => l.kind === "blocks" && l.dir === "in" && l.status === "open");

/** Blocked: an open item that an open item blocks. */
export const isBlocked = (it: { status: Status }, links: BoardLink[]) => it.status === "open" && blockersOf(links).length > 0;

const LINK_ORDER = ["blocks:in", "blocks:out", "duplicates:out", "duplicates:in", "relates:out", "relates:in"];

/** A card's links in reading order: what blocks it first, then what it blocks, duplicates, relates; oldest first within each. */
export const sortLinks = <L extends Pick<BoardLink, "kind" | "dir" | "at">>(links: L[]): L[] =>
  [...links].sort((a, b) => LINK_ORDER.indexOf(`${a.kind}:${a.dir}`) - LINK_ORDER.indexOf(`${b.kind}:${b.dir}`) || a.at.localeCompare(b.at));

/**
 * Changing an item's title and description: whoever logged it (an item their AI logged counts as
 * theirs), and leads and admins. On a personal board its owners and editors act as leads (orgViewer).
 */
export function canEditItem(v: Pick<Viewer, "id" | "role" | "isAdmin" | "org"> | null | undefined, it: { loggedById?: string | null }): boolean {
  if (!v || !can(v, "edit")) return false;
  return v.isAdmin || v.role === "lead" || (!!it.loggedById && it.loggedById === v.id);
}

/**
 * Setting or clearing an item's due date (OEM-267): whoever may change its title and description
 * (whoever logged it, leads and admins: canEditItem), on a personal board its owners and editors.
 * An archived item keeps its date as it was.
 */
export function canSetDue(v: Pick<Viewer, "id" | "role" | "isAdmin" | "org"> | null | undefined, it: { loggedById?: string | null; kind?: ItemKind | null }): boolean {
  return canEditItem(v, it);
}

/**
 * Making an item a draft, or ready for the team (OEM-286): whoever logged it (an item their AI logged is
 * theirs), leads and admins; on a personal board its owners and editors. Viewers never.
 */
export function canSetDraft(v: Pick<Viewer, "id" | "role" | "isAdmin" | "org"> | null | undefined, it: { loggedById?: string | null }): boolean {
  if (!v || !can(v, "draft")) return false;
  return v.isAdmin || v.role === "lead" || (!!it.loggedById && it.loggedById === v.id);
}

// -- Drafts (OEM-286) --------------------------------------------------------------------------
//
// "If Kal and Jake add something but they're not ready to discuss it with the team yet, they set it as a
// draft, which is hidden by default for others. When you click Show drafts, it shows everything." A draft
// gets its numbers as usual and isn't secret (a link opens it); the board, its counts, For you, What's
// new, the brief, reminders and Claude's lists leave it out for everyone but its author until it's ready.

/** The Filters popover's Drafts: others' drafts hidden (yours show), every draft shown too, or only drafts. */
export type DraftsFilter = "hide" | "show" | "only";
export const DRAFTS_FILTERS: { id: DraftsFilter; label: string; hint: string }[] = [
  { id: "hide", label: "Hide others'", hint: "Your own drafts show; other people's stay hidden until they're ready" },
  { id: "show", label: "Show drafts", hint: "Everyone's drafts too, marked Draft" },
  { id: "only", label: "Only drafts", hint: "Only items marked Draft, everyone's" },
];
export const isDraftsFilter = (v: unknown): v is DraftsFilter => v === "hide" || v === "show" || v === "only";

/** Whose draft it is: whoever logged it, and whoever made it a draft. */
export const isDraftAuthor = (it: { loggedById?: string | null; draft?: { byId: string | null } | null }, memberId: string | null | undefined) =>
  !!memberId && (it.loggedById === memberId || it.draft?.byId === memberId);

/** A draft someone else wrote: hidden from this person by default. */
export const draftHiddenFor = (it: { loggedById?: string | null; draft?: { byId: string | null } | null }, memberId: string | null | undefined) =>
  !!it.draft && !isDraftAuthor(it, memberId);

/** Whether an item shows under the Drafts filter, for this person. */
export function draftShows(it: { loggedById?: string | null; draft?: { byId: string | null } | null }, drafts: DraftsFilter | null | undefined, memberId: string | null | undefined): boolean {
  if (drafts === "only") return !!it.draft;
  if (drafts === "show") return true;
  return !draftHiddenFor(it, memberId);
}

/**
 * Changing a saved view (OEM-268: rename it, change its filters, reorder, delete): your own views, always
 * (viewers too, like pins); a view shared with the board, whoever may share views (can "view_shared":
 * leads on OEMUAP, owners and editors on a personal board).
 */
export function canEditView(v: (Pick<Viewer, "id" | "role" | "isAdmin" | "org">) | null | undefined, view: { ownerId: string | null }): boolean {
  if (!v) return false;
  return view.ownerId != null ? view.ownerId === v.id : can(v, "view_shared");
}

/** Editing a note: its author only (a note their AI wrote for them is theirs). Nobody else, leads and admins included. */
export function canEditNote(v: Pick<Viewer, "role" | "isAdmin" | "org"> | null | undefined, n: { mine: boolean }): boolean {
  return !!v && can(v, "note") && n.mine;
}

/** Removing a note (it's kept, marked removed, and the history says so): its author, or an admin. */
export function canRemoveNote(v: Pick<Viewer, "role" | "isAdmin" | "org"> | null | undefined, n: { mine: boolean }): boolean {
  return !!v && can(v, "note") && (n.mine || v.isAdmin);
}

/**
 * Adding screenshots or recordings to a note (they show under it): whoever may add files to items,
 * and only to their own notes (a note their AI wrote for them is theirs), like editing it.
 */
export function canAttachToNote(v: Pick<Viewer, "role" | "isAdmin" | "org"> | null | undefined, n: { mine: boolean }): boolean {
  return !!v && can(v, "attach") && n.mine;
}

/** Removing a link: whoever made it, leads and admins (and only while they may change items). */
export function canUnlink(v: (Pick<Viewer, "id" | "role" | "isAdmin"> & { canBuild?: boolean }) | null | undefined, link: { byId: string | null }): boolean {
  if (!v || !can(v, "organize")) return false;
  return v.isAdmin || v.role === "lead" || (!!link.byId && link.byId === v.id);
}

// -- Media (screenshots and screen recordings) --------------------------------

/** `unscanned`: imported before scanning existed. `pending`: being checked for credentials.
 *  `quarantined`: text read from it looks like a credential. */
export type ScanState = "unscanned" | "pending" | "clean" | "quarantined";

/** Media still being scanned, or hidden because it looks like it holds a credential, is shown
 *  only to whoever uploaded it and to admins. Everything else is shown to every member. */
export function canSeeMedia(
  v: Pick<Viewer, "id" | "isAdmin"> | null | undefined,
  a: { scan: ScanState; uploadedById: string | null },
): boolean {
  if (!v) return false;
  if (a.scan === "clean" || a.scan === "unscanned") return true;
  return v.isAdmin || (!!a.uploadedById && a.uploadedById === v.id);
}

/** Removing a screenshot: whoever uploaded it, or an admin (imported ones have no uploader). */
export function canRemoveMedia(v: Pick<Viewer, "id" | "isAdmin"> | null | undefined, a: { uploadedById: string | null }): boolean {
  if (!v) return false;
  return v.isAdmin || (!!a.uploadedById && a.uploadedById === v.id);
}

/** What the uploader reads on the card when their upload was hidden. */
export function quarantineNotice(labels: string[]): string {
  const what = labels.length ? ` (${labels.join(", ")})` : "";
  return `Hidden: this looks like it contains a credential${what}. Blur it and upload again.`;
}

// -- Board payload ----------------------------------------------------------

export interface BoardMember {
  id: string;
  name: string;
  role: MemberRole;
  isBot: boolean;
}

/** How something arrived from outside the web app, e.g. a Teams message. */
export interface BoardOrigin {
  /** route: teams_action | claude | claude_code | routine | web */
  via: string;
  /** link back to the original message */
  url: string | null;
  place: string | null;
  /** who said it (the Teams message author) */
  said: string | null;
  /** who put it on the board */
  filedBy: string;
  /** Claude drafted the text the filer confirmed */
  aiDrafted: boolean;
  at: string;
  sentAt: string | null;
  /** the original message, credentials redacted */
  text: string;
}

export const VIA_LABEL: Record<string, string> = {
  teams_action: "Teams",
  claude: "Claude",
  claude_code: "Claude Code",
  routine: "Claude routine",
  web: "the board",
  // a note sent, or an item created, from someone's personal board (orgs.md 6.1)
  personal_board: "Personal board",
  // a comment or review on a linked pull request (docs/github.md)
  github: "GitHub",
};

/**
 * Written by someone's AI: the note shows under the person's name with an
 * "AI · Claude" chip. `verified`: the app was recognised from how it signed in
 * (not just the name it gave). `how` explains it, for a tooltip.
 */
export interface AgentTag {
  app: string;
  verified: boolean;
  how: string;
}

/** "AI · Claude" */
export const agentChip = (a: Pick<AgentTag, "app">) => `AI · ${a.app}`;

export interface BoardNote {
  id: string;
  /** the person - for an AI's note, the person it works for */
  name: string;
  body: string;
  at: string;
  mine: boolean;
  origin: BoardOrigin | null;
  agent: AgentTag | null;
}

export interface BoardItem {
  num: number;
  title: string;
  body: string;
  status: Status;
  priority: Priority;
  tags: string[];
  role: string | null;
  flag: string | null;
  nondev: boolean;
  openedBy: string | null;
  loggedBy: string | null;
  /** who logged it, by member id (for an item their AI logged, the person): they may edit its title and description (canEditItem) */
  loggedById?: string | null;
  addedAt: string;
  closedAt: string | null;
  closedBy: string | null;
  closeNote: string | null;
  /** the lead of the people it's with (the first of `assignees`); `note`: why it was handed over and what the next step is */
  assignee: BoardAssignee | null;
  /** everyone it's with, the lead first (OEM-285); empty: nobody. Read it with assigneesOf (older payloads lack it) */
  assignees?: BoardAssignee[];
  /** every change of hands, oldest first */
  handoffs: Handoff[];
  /** the fix was agreed or confirmed in a Teams thread or call */
  teamsResolved: boolean;
  /** the fix flow: to do -> developed -> deployed -> verified (see laneOf) */
  stage: Stage;
  developed: StageStamp | null;
  deployed: StageStamp | null;
  verified: StageStamp | null;
  /** off the board, in the archive */
  archived: { at: string; by: string | null } | null;
  reactions: { name: string; kind: ReactionKind; at: string; agent: AgentTag | null }[];
  notes: BoardNote[];
  /** screenshots and screen recordings this viewer may see (see canSeeMedia) */
  shots: BoardShot[];
  /** someone has formed a view on it: a reaction, a note, a status change or an assignment */
  reviewed: boolean;
  /** set when the item itself was filed from outside the board */
  origin: BoardOrigin | null;
  /** people working on it right now (open work sessions with recent progress) */
  sessions?: WorkSessionView[];
  /** project / feature / bug / task; null = not classified yet */
  kind: ItemKind | null;
  /** what it sits under: a project, or for a sub-item its feature, bug or task */
  parent: ItemPlace | null;
  /** the project it's in (through its parent for a sub-item); null for a project itself */
  project: (ItemPlace & { ownBoard: boolean; projectNo: number | null }) | null;
  /** its number in that project ("01-07"); null outside projects */
  key: ItemKey | null;
  /** numbers it had in projects it has left ("01-03"): reserved for it, and they still find it */
  formerKeys: string[];
  /** a project's own number (01, 02, ...) */
  projectNo: number | null;
  /** an admin locked it to its project */
  locked: boolean;
  /** a project with a board of its own: its items stay off the main board */
  ownBoard: boolean;
  /**
   * How far along what's under it is: `total` sub-items and how many are `done` (closed). Archived
   * ones that aren't done are left out of both (archiving isn't finishing) and counted in `archived`;
   * archived done ones (verified) count as done. childCount() is everything under it.
   */
  children: { total: number; done: number; archived?: number };
  /** its links to other items (blocks / blocked by / relates to / duplicates / duplicated by), in reading order */
  links: BoardLink[];
  /** open, and an open item blocks it (see isBlocked) */
  blocked: boolean;
  /** boards it was moved from by a platform admin, and its numbers there ("was Gautam's #5 · 01-03"), as text from then */
  movedFrom?: { board: string; num: number; key: string | null; at: string }[];
  /** where it was copied from ("Copy to my board"): the text from then, and a link back while the reader can open that board */
  copiedFrom?: { board: string; num: number; key: string | null; href: string | null; closed: boolean } | null;
  /** a personal item's link to an OEMUAP item (orgs.md 6.1): the On OEMUAP block. Personal boards only. */
  bridge?: BoardBridge | null;
  /** the pull requests linked to it (docs/github.md). OEMUAP only: a personal board has no GitHub. */
  prs?: BoardPr[];
  /** the day it's due ("2026-10-21", a calendar day in the board's time zone); null: no due date (OEM-267, due.ts) */
  due?: string | null;
  /** a draft (OEM-286): since when and who made it one; null = ready for the team */
  draft?: { at: string; by: string | null; byId: string | null } | null;
}

// -- GitHub (docs/github.md) -----------------------------------------------------------------
//
// OEMUAP items are named in GitHub with the company code: OEM-185 (the # number, which never changes)
// in branch names, PR titles, PR bodies and commit messages; OEM-03-01 (a project number) works too.
// A bare #185 is GitHub's own (its issue and PR numbers), so the board ignores it. Personal boards
// have no code and no GitHub.

/** The company code in references: OEM-185. */
export const COMPANY_CODE = "OEM";

/**
 * The board's member that files what comes from GitHub (a PR's comments and reviews, as notes): "filed
 * by GitHub", while "said by" is the comment's author. Not a person, and not an AI: it never signs in.
 */
export const GITHUB_MEMBER = "GitHub";

/** A reference found in GitHub text: OEM-185, or OEM-03-01 (a project number). */
export interface GitHubRef {
  /** the # number: OEM-185; for OEM-185-404 also the # it means if there is no item 185-404 (null when that can't be a #) */
  num: number | null;
  /** OEM-03-01: project 3, item 1 */
  key: { projectNo: number; seq: number } | null;
}

const REF_RE = /(?<![A-Za-z0-9])OEM-(\d{1,6})(?:-(\d{1,6}))?(?![A-Za-z0-9])/gi;

/**
 * The item references in a piece of GitHub text (a branch name, a PR title or body, a commit message),
 * each once, in order. "OEM-185-fix-login" is #185 (the words after it aren't digits); "OEM-03-01" is
 * project 03's item 01; "OEM-12-05" is project 12's item 05 if that exists, else #12 (a project
 * number written without its leading zero is ambiguous; one with it never means a #). Bare "#185"
 * and "OEM185" are not references. Pure.
 */
export function parseRefs(text: string | null | undefined): GitHubRef[] {
  if (!text) return [];
  const out: GitHubRef[] = [];
  const seen = new Set<string>();
  for (const m of text.matchAll(REF_RE)) {
    const a = m[1]!;
    const b = m[2];
    const n = Number(a);
    if (!(n > 0)) continue;
    let ref: GitHubRef;
    if (b != null) {
      const seq = Number(b);
      if (!(seq > 0)) continue;
      ref = { num: a.startsWith("0") ? null : n, key: { projectNo: n, seq } };
    } else {
      if (a.startsWith("0")) continue;
      ref = { num: n, key: null };
    }
    const id = `${ref.num}|${ref.key ? `${ref.key.projectNo}-${ref.key.seq}` : ""}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(ref);
  }
  return out;
}

/** "OEM-185": how GitHub text names an OEMUAP item (by its # number, which never changes). */
export const githubRef = (num: number) => `${COMPANY_CODE}-${num}`;

/** A branch name for an item: "OEM-185-short-title" (3-5 words of the title, at most 40 characters of them). Pure. */
export function branchName(it: { num: number; title: string }): string {
  const words = it.title
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 5);
  let slug = "";
  for (const w of words) {
    const next = slug ? `${slug}-${w}` : w;
    if (next.length > 40) break;
    slug = next;
  }
  return slug ? `${githubRef(it.num)}-${slug}` : githubRef(it.num);
}

/** What "Copy for PR description" puts on the clipboard: the reference and a link back to the board. Pure. */
export function prDescription(it: { num: number; title: string; key?: { label: string } | null }, boardUrl: string): string {
  const base = boardUrl.replace(/\/+$/, "");
  const name = it.key ? `${it.key.label} (#${it.num})` : `#${it.num}`;
  return `${githubRef(it.num)}: ${it.title}\n\nBoard: [${name}](${base}/#item-${it.num})`;
}

/** The autolink to set up on each repo (the read-only app can't set it): OEM-185 links to the item. */
export const autolinkFor = (boardUrl: string) => ({ prefix: `${COMPANY_CODE}-`, urlTemplate: `${boardUrl.replace(/\/+$/, "")}/#item-<num>` });

export type PrState = "open" | "closed" | "merged";
export type CiState = "pending" | "success" | "failure";

/** A pull request linked to an item, as its card shows it. */
export interface BoardPr {
  id: string;
  /** "oemuap/mycom-next" */
  repo: string;
  number: number;
  /** credentials redacted */
  title: string;
  url: string;
  state: PrState;
  draft: boolean;
  /** its CI on the current head; null: none seen */
  ci: CiState | null;
  /** who opened it: their name on the board, else their GitHub username */
  author: string;
  authorLogin: string;
  openedAt: string;
  mergedAt: string | null;
  lastActivityAt: string;
  baseRef: string;
  /** merged into the repo's default branch */
  intoDefault: boolean;
  /** the tag (or deploy) it went out in */
  deployedIn: string | null;
  deployedAt: string | null;
  /** the gate lets its repo move the item (linked to the item's project; or, for an item in no
   *  project, to the main board): the board moves the item for it */
  moves: boolean;
  /** how it was linked: branch | title | body | commit | manual */
  via: string;
  /** open with no activity for the board's stale days */
  stale: boolean;
  /** PR review: who GitHub asks to review it now (by their board name when known) */
  reviewers: PrPerson[];
  /** who it waits for while it's in review: its requested reviewers, else the default PR reviewer (Settings → GitHub) */
  waitingFor: PrPerson[];
  /** the reviews' verdict so far: the latest of each reviewer, changes requested over approved */
  review: "approved" | "changes_requested" | null;
  /** when it became ready for review (null while a draft) */
  readyAt: string | null;
  /** open, ready, and nobody has reviewed it for the board's stale days since it was ready (or last reviewed) */
  reviewStale: boolean;
}

/** Someone a PR names: their board name and id when they're on the team, else their GitHub username. */
export interface PrPerson {
  login: string;
  id: string | null;
  name: string;
}

/** "3d", "5h", "20m": how long ago, short (for a chip). Pure. */
export function ageShort(fromIso: string, now: Date = new Date()): string {
  const min = Math.max(0, Math.floor((now.getTime() - new Date(fromIso).getTime()) / 60_000));
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
}

/** An open PR with no activity for `days` days. Pure. */
export const isStalePr = (pr: Pick<BoardPr, "state" | "lastActivityAt">, days: number, now: Date = new Date()) =>
  pr.state === "open" && now.getTime() - new Date(pr.lastActivityAt).getTime() >= days * 86_400_000;

/** A review's verdict as the chip and the history say it. */
export const REVIEW_WORDS = { approved: "approved", changes_requested: "changes requested" } as const;

/** The chip's text: "PR 418 · open 3d · draft", "PR 418 · merged", "PR 418 · open 1d · approved · CI failing". Pure. */
export function prChip(pr: Pick<BoardPr, "number" | "state" | "draft" | "ci" | "openedAt" | "stale"> & { review?: BoardPr["review"] }, now: Date = new Date()): string {
  const parts = [`PR ${pr.number}`];
  if (pr.state === "open") parts.push(`open ${ageShort(pr.openedAt, now)}`);
  else parts.push(pr.state);
  if (pr.state === "open" && pr.draft) parts.push("draft");
  if (pr.state === "open" && pr.review) parts.push(REVIEW_WORDS[pr.review]);
  if (pr.ci === "failure" && pr.state !== "closed") parts.push("CI failing");
  if (pr.stale) parts.push("stale");
  return parts.join(" · ");
}

/** A PR that puts its item in the PR review queue: open, not a draft, and from a repo linked to the item's project. */
export const inReviewPr = (p: Pick<BoardPr, "state" | "draft" | "moves">) => p.state === "open" && !p.draft && p.moves;

/**
 * "PR review": a fix (a feature, a bug, or an item not classified yet) still To do with at least one
 * linked PR that is open, not a draft, and from a repo linked to its project. Not a stored stage
 * (docs/github.md): it's read from the linked PRs, so the fix flow and its rules stay as they are,
 * and the item sits in the PR review queue between To do and the deploy queue. A draft alone keeps it
 * To do (its chip says draft); a task with a PR stays To do (tasks go To do → Done) and shows its chip. Pure.
 */
export function inReview(it: { status: Status; stage: Stage; kind?: ItemKind | null; archived?: unknown; prs?: Pick<BoardPr, "state" | "draft" | "moves">[] }): boolean {
  return it.status === "open" && !it.archived && it.stage === "todo" && kindFlow(it.kind) === "fix" && !!it.prs?.some(inReviewPr);
}

/** Who an item in PR review waits for: everyone its review PRs wait for, each once. Pure. */
export function reviewWaitingFor(it: { prs?: (Pick<BoardPr, "state" | "draft" | "moves" | "waitingFor">)[] }): PrPerson[] {
  const out = new Map<string, PrPerson>();
  for (const p of it.prs ?? []) if (inReviewPr(p)) for (const w of p.waitingFor) out.set(w.id ?? w.login.toLowerCase(), w);
  return [...out.values()];
}

/** An item in PR review that waits for someone, with the PR it waits on. */
export interface ReviewWait<I> {
  item: I;
  pr: BoardPr;
}

/**
 * The items in PR review that wait for this person (a requested reviewer, or the default PR reviewer
 * when nobody is requested): For you's "PRs waiting for your review" and Claude's my_queue. The longest
 * waiting first. Pure.
 */
export function reviewsWaitingFor<I extends Pick<BoardItem, "num" | "status" | "stage" | "kind" | "archived" | "prs">>(items: I[], viewerId: string): ReviewWait<I>[] {
  const out: ReviewWait<I>[] = [];
  for (const it of items) {
    if (!inReview(it)) continue;
    for (const pr of it.prs ?? []) if (inReviewPr(pr) && pr.waitingFor.some((w) => w.id === viewerId)) out.push({ item: it, pr });
  }
  return out.sort((a, b) => (a.pr.readyAt ?? a.pr.openedAt).localeCompare(b.pr.readyAt ?? b.pr.openedAt) || a.item.num - b.item.num);
}

/**
 * A repo's deploy signal (Settings → GitHub, admins; per repo): a version tag pushed, a deploy job
 * succeeding, or each push to the default branch (a host that deploys every push, as App Platform does
 * the board's).
 */
export type DeployRule = { kind: "tag"; pattern: string } | { kind: "job"; workflow: string; job: string } | { kind: "push" };

/** A tag pattern ("prod-*", "v*") as a regular expression: * is anything, ? one character. Pure. */
export function tagMatcher(pattern: string): RegExp {
  const body = pattern
    .split("")
    .map((c) => (c === "*" ? ".*" : c === "?" ? "." : c.replace(/[\\^$.|+()[\]{}]/g, "\\$&")))
    .join("");
  return new RegExp(`^${body}$`);
}

/** Why a deploy rule can't be saved, or null. Pure. */
export function deployRuleProblem(r: DeployRule): string | null {
  if (r.kind === "push") return null;
  if (r.kind === "tag") {
    const p = r.pattern.trim();
    if (!p) return "Give the tag pattern, e.g. prod-* or v*.";
    if (p.length > 100 || /\s/.test(p)) return "A tag pattern has no spaces and is under 100 characters.";
    return null;
  }
  if (!/^[\w.-]+\.ya?ml$/.test(r.workflow.trim())) return "Give the workflow's file name, e.g. prod-release.yaml.";
  if (!r.job.trim() || r.job.trim().length > 100) return "Give the job's name, e.g. deploy.";
  return null;
}

/** "version tag prod-*", "job deploy in prod-release.yaml", "each push to the default branch". Pure. */
export const deployRuleLabel = (r: DeployRule | null) =>
  !r ? "not set" : r.kind === "tag" ? `version tag ${r.pattern}` : r.kind === "job" ? `job ${r.job} in ${r.workflow}` : "each push to the default branch";

/**
 * The release a change to a push-deployed repo went out in (What's new groups deployed items by it):
 * the commit's short sha and the day, "a1b2c3d (2026-10-07)". Pure.
 */
export const pushRelease = (sha: string, at: Date) => `${sha.slice(0, 7)} (${at.toISOString().slice(0, 10)})`;

/** A repo in Settings → GitHub and Project settings → Repos. */
export interface GitHubRepoView {
  /** GitHub's id, as a string */
  id: string;
  fullName: string;
  defaultBranch: string;
  private: boolean;
  archived: boolean;
  inInstallation: boolean;
  /** the projects it's linked to */
  projects: { num: number; title: string; projectNo: number | null }[];
  /** linked to the main board (its PRs move the items in no project): by whom and when; null: not */
  mainBoard: { by: string | null; at: string } | null;
  deploy: DeployRule | null;
  /** "Deployed isn't detected": something uses it (the main board or a project) and it has no deploy
   *  signal, so its items stop at Developed (deployUndetected) */
  noDeploy: boolean;
  /** the OEM- autolink: confirmed by someone (the read-only app can't read autolinks), or not yet */
  autolink: { confirmed: boolean; by: string | null; at: string | null };
  /** its autolink settings page on GitHub */
  autolinkUrl: string;
}

/** Something uses the repo (the main board, or a project) but it has no deploy signal: its items stop at Developed. Pure. */
export const deployUndetected = (r: { mainBoard: unknown; projects: unknown[]; deploy: DeployRule | null }) => (!!r.mainBoard || r.projects.length > 0) && !r.deploy;

/** Settings → GitHub's repos (admins and leads): every repo the installation has, once, with what uses it. */
export interface BoardReposView {
  /** GitHub is connected and installed */
  connected: boolean;
  /** where the list came from: the installation (GitHub), or only the repos the board knows (GitHub didn't answer) */
  listed: "installation" | "board";
  repos: GitHubRepoView[];
  /** the projects a repo can be linked to (the "Used by" picker) */
  projects: { num: number; title: string; projectNo: number | null; ownBoard: boolean }[];
}

/** A repo found by searching the organisation's repos (through the installation). */
export interface RepoHit {
  id: string;
  fullName: string;
  defaultBranch: string;
  private: boolean;
  archived: boolean;
}

/** A webhook delivery, as Settings → GitHub lists it. */
export interface GitHubDeliveryView {
  id: string;
  event: string;
  action: string | null;
  repo: string | null;
  at: string;
  processed: boolean;
  summary: string | null;
  error: string | null;
  attempts: number;
}

/** Settings → GitHub (OEMUAP admins). */
export interface GitHubStatus {
  /** BOARD_SECRET_KEY is set (the app's secrets are stored encrypted with it): Connect needs it */
  secretKey: boolean;
  /** the GitHub organisation Connect makes the app for */
  defaultOwner: string;
  app: {
    name: string;
    slug: string;
    owner: string;
    htmlUrl: string;
    appId: number;
    installUrl: string;
    installed: boolean;
    repoSelection: string | null;
    connectedAt: string;
    connectedBy: string | null;
    staleDays: number;
    /** who a PR in review waits for when nobody is requested on GitHub (null: nobody) */
    defaultReviewer: { id: string; name: string } | null;
    /** what the installation hasn't accepted on GitHub yet (an app connected before the board asked for
     *  them): the owner accepts them on GitHub. null: the board couldn't ask GitHub */
    missing: { permissions: string[]; events: string[] } | null;
    /** the app's "Permissions & events" page on GitHub, where what the board asks for is added */
    permissionsUrl: string;
    /** the installation's page on GitHub, where the organisation's owner accepts them */
    acceptUrl: string;
  } | null;
  code: string;
  autolink: { prefix: string; urlTemplate: string };
  lastWebhookAt: string | null;
  deliveries: GitHubDeliveryView[];
  /** deliveries in the last 7 days that failed */
  failures: number;
  people: { id: string; name: string; githubLogin: string | null; isBot: boolean }[];
}

/** A project's repos (Project settings → Repos). */
export interface ProjectReposView {
  connected: boolean;
  repos: (GitHubRepoView & { addedBy: string | null; addedAt: string })[];
}

/** How many commits a scan of recent commits reads (Project settings → Repos → Scan recent commits). */
export const SCAN_COMMITS = 20;

/** What a scan of recent commits would do to one item (docs/github.md). */
export interface ScanItemChange {
  num: number;
  /** "01-07 (#185)", as Claude and Teams name it */
  label: string;
  title: string;
  /** where it is now: "To do", "PR review", "Developed", "Deployed", "Verified", "Done", "Closed" */
  from: string;
  /** where Apply would put it (the same as `from` when it doesn't move) */
  to: string;
  /** what Apply would write, in words: "links PR 205; To do → Developed → Deployed" */
  what: string;
  /** false: Apply changes nothing for it (a webhook already did it, or nothing applies) */
  changes: boolean;
}

/** A commit a scan read, newest first. */
export interface ScanCommitView {
  sha: string;
  short: string;
  /** its message's first line, credentials redacted */
  message: string;
  /** their board name, else their GitHub username, else the name in the commit */
  author: string;
  url: string | null;
  /** the PR it came in with, if any */
  pr: { number: number; url: string; title: string; state: PrState; draft: boolean } | null;
  /** the PR's items are listed with the newest of its commits; its other commits say so instead */
  samePr: boolean;
  items: ScanItemChange[];
}

/** A scan of a repo's recent commits, before Apply. */
export interface RepoScanView {
  /** what Apply names (the read is kept on the server for a while; an expired one is read again) */
  id: string;
  repoId: string;
  repo: string;
  defaultBranch: string;
  /** a project's scan (its #): only that project's items are listed and applied; null: the whole board */
  project: number | null;
  deploy: DeployRule | null;
  /** what the deploy signal says, in words: "prod-26.10.02.1 has the commits up to a1b2c3d" */
  deploySaid: string;
  commits: ScanCommitView[];
  /** open PRs (beyond the commits) that name items */
  openPrs: { number: number; url: string; title: string; draft: boolean; author: string; items: ScanItemChange[] }[];
  /** merged PRs from before these commits, not deployed yet, that Apply checks against the deploy */
  olderPending: number;
  /** how many items would change */
  changes: number;
  /** how many GitHub reads the scan made (cached commits aren't read again) */
  reads: number;
  at: string;
}

/** What Apply did. */
export interface RepoScanApplied {
  /** history lines written, each saying it was found by a scan */
  lines: number;
  /** what happened, a line per step */
  said: string[];
}

/** A recent PR in a project's repos that no item has: the project's board offers to attach it. */
export interface UnlinkedPr {
  id: string;
  repo: string;
  number: number;
  title: string;
  url: string;
  state: PrState;
  draft: boolean;
  author: string;
  openedAt: string;
}

/** How a personal item came to be linked to its OEMUAP item. */
export type BridgeKind = "linked" | "cloned_to" | "cloned_from";

/**
 * A personal item's link to an OEMUAP item, as its card shows it ("On OEMUAP"). It lives on the
 * personal board only; the OEMUAP item never knows. The OEMUAP side is read live, as the reader's own
 * OEMUAP access allows.
 */
export interface BoardBridge {
  id: string;
  kind: BridgeKind;
  /** how OEMUAP names its item now: "02-03" or "#103" */
  label: string;
  /** its # on OEMUAP */
  num: number;
  /** the OEMUAP item as it is now; null when the reader can't open OEMUAP (or the item is gone) */
  target: {
    title: string;
    kind: ItemKind | null;
    stage: Stage;
    status: Status;
    assignee: string | null;
    archived: boolean;
  } | null;
  /** opens it on OEMUAP, in a new tab (a company item never opens inside a personal board) */
  href: string;
  linkedAt: string;
  /** when updates were last sent through this link */
  lastSent: string | null;
  /** how many updates are waiting to be sent (Send updates · n) */
  waiting: number;
}

/** An open work session, e.g. someone working the item through Claude Code. */
export interface WorkSessionView {
  id: string;
  member: string;
  /** event source it was started from: claude_code | mcp | web */
  source: string;
  /** the AI app it runs in, when known (Claude, ChatGPT, ...) */
  agentApp: string | null;
  branch: string | null;
  startedAt: string;
  lastProgressAt: string;
  percent: number | null;
  lastProgress: string | null;
}

/** How a work session was started, for "working via ..." */
/** "Claude Code" for a session: its AI app when known, else from where it was started. */
export const workVia = (s: Pick<WorkSessionView, "source" | "agentApp">) => s.agentApp ?? WORK_VIA_LABEL[s.source] ?? s.source;

export const WORK_VIA_LABEL: Record<string, string> = {
  claude_code: "Claude Code",
  mcp: "Claude",
  web: "the board",
};

/** "12 min", "3 h", "2 d" between two instants (default: until now). */
export function elapsed(fromIso: string, to: Date = new Date()): string {
  const min = Math.max(0, Math.round((to.getTime() - new Date(fromIso).getTime()) / 60_000));
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} d`;
}

export interface BoardShot {
  id: string;
  /** small image: the screenshot's thumbnail, or a video's poster thumbnail */
  thumb: string;
  /** the full image or the video itself; null = thumbnail only (the original board kept no full size) */
  full: string | null;
  kind: "image" | "video";
  /** video: a full-size frame shown before it plays */
  poster: string | null;
  /** video length in seconds */
  duration: number | null;
  scan: ScanState;
  /** quarantined: what the scan found (labels only, never the secret) */
  findings: string[];
  /** the scan couldn't run; the file stays hidden from others until it does */
  scanFailed: boolean;
  uploadedBy: string | null;
  /** the viewer uploaded it */
  mine: boolean;
  /** the file name it was uploaded with */
  name: string | null;
  /** added with this note (it shows under the note); null = on the item itself */
  noteId: string | null;
  /** a direct upload being made ready: no thumbnail yet (its uploader and admins see it as checking) */
  processing?: boolean;
  /** a direct upload that arrived but couldn't be used, and why (its uploader and admins, until removed) */
  failed?: string;
}

export interface Handoff {
  at: string;
  from: string | null;
  /** null = unassigned */
  to: string | null;
  by: string | null;
  note: string | null;
}

/** Added via Teams: tagged `teams` (the original board's marker) or filed through the Teams action. */
export const teamsIn = (it: Pick<BoardItem, "tags" | "origin">) => it.tags.includes("teams") || it.origin?.via === "teams_action";

/** Off the main board: a project with a board of its own, and everything in it. */
export const ownBoardOnly = (it: Pick<BoardItem, "kind" | "ownBoard" | "project">) => (it.kind === "project" && it.ownBoard) || !!it.project?.ownBoard;

/**
 * The board to go to for an item ("Go to its board", ↗ on a row): its project's board when it's
 * in a project (for a project, its own), else the main board (null). Unlike boardOf, this is any
 * project's board, not only one with a board of its own: there the item is with the rest of
 * its project.
 */
export const goToBoardOf = (it: Pick<BoardItem, "num" | "kind" | "project">): number | null => (it.kind === "project" ? it.num : (it.project?.num ?? null));

/** Which board an item shows on: a project's own board (its number), or the main board (null). */
export const boardOf = (it: Pick<BoardItem, "num" | "kind" | "ownBoard" | "project">): number | null =>
  it.kind === "project" && it.ownBoard ? it.num : it.project?.ownBoard ? it.project.num : null;

export interface Reviewer {
  name: string;
  /** someone's AI (e.g. the original artifact's Claude pass), counted apart from the person */
  agent: AgentTag | null;
  items: number;
  closed: number;
}

export interface Board {
  /** which board this is (OEMUAP, or a personal board) */
  org: BoardOrg;
  /** the person as this board sees them (orgViewer): their role here decides what the UI offers */
  viewer: Viewer;
  /** the board: everything not archived. The archive view gets the archived items instead. */
  items: BoardItem[];
  members: BoardMember[];
  reviewers: Reviewer[];
  /** how many items are in the archive */
  archivedCount: number;
  /** the board's time zone (REMINDER_TZ): "today" and "this week" for due dates are its */
  timeZone?: string;
}

export interface HistoryEntry {
  at: string;
  kind: string;
  source: string;
  actor: string | null;
  /** done by the actor's AI app */
  agent: AgentTag | null;
  text: string;
  /** a note's line ("added a note"): which note, so its screenshots can show with it */
  noteId?: string;
}

// -- Reminders and feature requests (phase 4a) --------------------------------

export type ReminderKind = "stale" | "unassigned" | "question" | "verify" | "weekly";

export const REMINDER_LABEL: Record<ReminderKind, string> = {
  stale: "Waiting on you",
  question: "Question for you",
  unassigned: "Nobody has it",
  verify: "Ready to verify",
  weekly: "Weekly summary",
};

export interface ReminderView {
  id: string;
  kind: ReminderKind;
  itemNum: number | null;
  text: string;
  at: string;
  read: boolean;
  /** false once it no longer applies (the item was closed, answered, handed on...) */
  current: boolean;
  /** also sent as a Teams message */
  teams: boolean;
}

export interface RemindersPayload {
  reminders: ReminderView[];
  /** unread reminders that still apply - the bell's number */
  unread: number;
  paused: boolean;
  /** Teams delivery: off until the bot is set up; then each person adds the app in Teams */
  teams: "off" | "not-installed" | "on";
}

export interface FeatureRequestView {
  id: string;
  title: string;
  items: number[];
  status: "pending" | "created" | "failed";
  issueUrl: string | null;
  issueNumber: number | null;
  error: string | null;
  at: string;
}

export interface RequestsPayload {
  github: { configured: boolean; repo: string | null; missing: string[] };
  requests: FeatureRequestView[];
}

// -- Team admin -----------------------------------------------------------------

export const ROLES: { id: MemberRole; label: string; can: string }[] = [
  { id: "lead", label: "Lead", can: "everything a dev can, plus mark items non-dev, and archive or restore them" },
  { id: "dev", label: "Dev", can: "log items, react, note, claim, assign anyone to any item and change who has it, mark developed / deployed / verified, close and reopen" },
  { id: "reporter", label: "Reporter", can: "log items, react, note, claim, change who has an item they are on, mark developed / deployed / verified, close and reopen" },
  { id: "viewer", label: "Viewer", can: "read only" },
];

export interface AdminMember {
  id: string;
  name: string;
  email: string | null;
  role: MemberRole;
  isAdmin: boolean;
  /** admin because their email is in ADMIN_EMAILS on the server - can't be changed here */
  adminByEnv: boolean;
  canBuild: boolean;
  canApprove: boolean;
  /** checks fixes after they're deployed (the verify queue and its reminders) */
  isVerifier: boolean;
  isBot: boolean;
  active: boolean;
  createdAt: string;
  /** open items still assigned to them */
  openItems: number;
}

export interface MemberEventView {
  at: string;
  member: string;
  actor: string | null;
  source: string;
  kind: string;
  text: string;
}

export interface TeamAdmin {
  members: AdminMember[];
  /** newest first */
  events: MemberEventView[];
}

// -- Formatting -------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Jul 30, 2026" in the reader's own time zone. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** The text a lead pastes to a developer - same shape as the original "Copy report". */
export function formatReport(items: BoardItem[], opts: { for?: string[]; today?: Date } = {}): string {
  const who = opts.for?.length ? ` for ${opts.for.join(", ")}` : "";
  const lines = [
    `Buy.OEM board — ${items.length} item${items.length === 1 ? "" : "s"}${who} — ${fmtDate((opts.today ?? new Date()).toISOString())}`,
  ];
  for (const it of items) {
    const last = it.notes.reduce<BoardNote | null>((a, n) => (!a || n.at > a.at ? n : a), null);
    lines.push("");
    lines.push(
      `#${it.num} · ${it.priority} · ${it.status === "open" ? STAGE_LABEL[it.stage].toLowerCase() : "closed"} · ${it.role ?? ""}` +
        (it.assignee ? ` · assigned to ${nameList(assigneeNames(it))}` : ""),
    );
    lines.push(`  ${it.title}`);
    if (last) lines.push(`  last note (${last.name}, ${fmtDate(last.at)}): ${last.body.replace(/\s+/g, " ").slice(0, 220)}`);
  }
  return lines.join("\n");
}
